import { encode, decode } from '@msgpack/msgpack';
import { MessageType } from '@use/shared';
import type { GameMessage, BaseMessage } from '@use/shared';

export type MessageCallback = (message: GameMessage) => void;

export class NetworkClient {
  private ws: WebSocket | null = null;
  private handlers: Map<MessageType, MessageCallback[]> = new Map();
  private connected = false;
  private serverUrl: string;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;

  public playerId: string = '';

  constructor(serverUrl?: string) {
    // Use production server on Fly.io, or localhost for dev
    const defaultUrl = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
      ? `ws://${window.location.hostname}:3001/ws`
      : `wss://theultrascorchedearth.fly.dev/ws`;
    this.serverUrl = serverUrl || defaultUrl;
  }

  public connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        this.ws = new WebSocket(this.serverUrl);
        this.ws.binaryType = 'arraybuffer';

        this.ws.onopen = () => {
          console.log('[Network] Connected to server');
          this.connected = true;
          this.reconnectAttempts = 0;
          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            let msg: GameMessage;
            if (event.data instanceof ArrayBuffer) {
              msg = decode(new Uint8Array(event.data)) as GameMessage;
            } else {
              msg = JSON.parse(event.data) as GameMessage;
            }
            this.dispatch(msg);
          } catch (e) {
            console.error('[Network] Failed to decode message:', e);
          }
        };

        this.ws.onclose = () => {
          console.log('[Network] Disconnected');
          this.connected = false;
          this.attemptReconnect();
        };

        this.ws.onerror = (err) => {
          console.error('[Network] Error:', err);
          if (!this.connected) reject(err);
        };
      } catch (e) {
        reject(e);
      }
    });
  }

  private attemptReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.log('[Network] Max reconnect attempts reached');
      return;
    }
    this.reconnectAttempts++;
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 10000);
    console.log(`[Network] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`);
    setTimeout(() => {
      this.connect().catch(() => {});
    }, delay);
  }

  public send(message: GameMessage): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;

    try {
      // Use msgpack binary encoding
      const data = encode(message);
      this.ws.send(data);
    } catch (e) {
      console.error('[Network] Failed to send message:', e);
    }
  }

  public sendJSON(message: GameMessage): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    this.ws.send(JSON.stringify(message));
  }

  public on(type: MessageType, callback: MessageCallback): void {
    if (!this.handlers.has(type)) {
      this.handlers.set(type, []);
    }
    this.handlers.get(type)!.push(callback);
  }

  public off(type: MessageType, callback: MessageCallback): void {
    const list = this.handlers.get(type);
    if (list) {
      const idx = list.indexOf(callback);
      if (idx >= 0) list.splice(idx, 1);
    }
  }

  private dispatch(message: GameMessage): void {
    const list = this.handlers.get(message.type);
    if (list) {
      for (const cb of list) {
        cb(message);
      }
    }
  }

  public isConnected(): boolean {
    return this.connected;
  }

  public disconnect(): void {
    this.maxReconnectAttempts = 0; // prevent reconnect
    this.ws?.close();
    this.ws = null;
    this.connected = false;
  }
}
